// Beam grouping and stem direction (§4.8).

import { type TimeSig, beatGroups, ticks, WHOLE } from '@simplenotation/theory';
import type { Beat } from '@simplenotation/core';

export function beamable(b: Beat): boolean {
  return !b.rest && !b.grace && b.notes.length > 0 && b.duration.base >= 8 && b.stem !== 'hidden';
}

/** Index of the beat group (from the time signature) containing a tick. */
export function groupIndexAt(groups: number[], tick: number): number {
  let acc = 0;
  for (let i = 0; i < groups.length; i++) {
    acc += groups[i];
    if (tick < acc - 1e-6) return i;
  }
  // Over-full bars: keep counting in the last group's size.
  const last = groups[groups.length - 1] || WHOLE / 4;
  return groups.length + Math.floor((tick - acc) / last);
}

/**
 * Beam groups for one voice of one bar: lists of beat indexes (only groups of
 * two or more beats are beamed). 4/4 joins 2+2 eighths across beats 1–2 and
 * 3–4 when there are no rests or shorter values.
 */
export function beamGroups(beats: readonly Beat[], onsets: readonly number[], ts: TimeSig): number[][] {
  const groups = beatGroups(ts);
  const out: number[][] = [];
  let cur: number[] = [];
  const close = () => {
    if (cur.length) out.push(cur);
    cur = [];
  };
  for (let i = 0; i < beats.length; i++) {
    const b = beats[i];
    if (b.grace) continue;
    if (!beamable(b)) {
      close();
      continue;
    }
    if (cur.length) {
      const prevIdx = cur[cur.length - 1];
      const prev = beats[prevIdx];
      const sameGroup = groupIndexAt(groups, onsets[i]) === groupIndexAt(groups, onsets[prevIdx]);
      const join = prev.beamBreak === 'join';
      const brk = prev.beamBreak === 'break';
      if (brk || (!sameGroup && !join)) close();
    }
    cur.push(i);
  }
  close();

  // 4/4: merge pairs of plain-eighth beat groups within each half bar.
  if (ts.num === 4 && ts.den === 4) {
    const plainEighths = (g: number[]) => g.length === 2 && g.every((i) => beats[i].duration.base === 8 && !beats[i].duration.dots && !beats[i].duration.tuplet);
    const merged: number[][] = [];
    for (let k = 0; k < out.length; k++) {
      const g = out[k];
      const h = out[k + 1];
      if (
        h &&
        plainEighths(g) &&
        plainEighths(h) &&
        g[1] + 1 === h[0] &&
        beats[g[1]].beamBreak !== 'break' &&
        Math.floor(onsets[g[0]] / (WHOLE / 2)) === Math.floor(onsets[h[1]] / (WHOLE / 2)) &&
        onsets[g[0]] % (WHOLE / 2) === 0
      ) {
        merged.push([...g, ...h]);
        k++;
      } else merged.push(g);
    }
    return merged;
  }
  return out;
}

/** Number of beams (flags) for a duration base: eighth = 1, 16th = 2 … */
export function beamCount(base: number): number {
  return base >= 8 ? Math.round(Math.log2(base / 4)) : 0;
}

/** Stem direction from staff positions: +1 = up, −1 = down. Average on/above the middle line → down. */
export function autoStemDir(positions: number[]): 1 | -1 {
  if (!positions.length) return 1;
  const avg = positions.reduce((a, b) => a + b, 0) / positions.length;
  return avg >= 0 ? -1 : 1;
}

/**
 * Tuplet groups: consecutive beats with the same ratio, chunked into complete
 * tuplets (normal × plain value of the first beat).
 */
export function tupletGroups(beats: readonly Beat[]): number[][] {
  const out: number[][] = [];
  let cur: number[] = [];
  let need = 0;
  let have = 0;
  for (let i = 0; i < beats.length; i++) {
    const b = beats[i];
    if (b.grace) continue;
    const t = b.duration.tuplet;
    if (!t) {
      if (cur.length) out.push(cur);
      cur = [];
      continue;
    }
    if (cur.length) {
      const first = beats[cur[0]].duration.tuplet!;
      if (first.actual !== t.actual || first.normal !== t.normal) {
        out.push(cur);
        cur = [];
      }
    }
    if (!cur.length) {
      need = t.normal * (WHOLE / b.duration.base);
      have = 0;
    }
    cur.push(i);
    have += ticks(b.duration);
    if (have >= need - 1e-6) {
      out.push(cur);
      cur = [];
    }
  }
  if (cur.length) out.push(cur);
  return out;
}
